import {
  Component,
  OnInit,
  Input,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
} from '@angular/core';
// Animations
import { slideInDownAnimation } from '../../app/app.animation';
// Common
import {
  AngularMaterialService,
  ErrorParseService,
  IEinConfiguration,
  LGEDataService,
  IFormConfiguration,
} from '../../commons';
// State
import { Store } from '@ngrx/store';
import * as fromEinConfiguration from './state/ein-configuration.selectors';
import * as fromAuth from '../login/state/login.selectors';
import * as EinConfigurationAction from './state/ein-configuration.actions';
import * as lgeApp from '../../app/state/app.actions';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import * as _ from 'lodash';
import { LoadFormConfigState } from '../login/state/login.actions';
import moment from 'moment';
import { firstValueFrom, Observable, skipWhile, Subscription } from 'rxjs';

@Component({
  selector: 'ein-configuration-screen',
  templateUrl: 'ein-configuration.component.html',
  styleUrls: ['ein-configuration.scss'],
  animations: [slideInDownAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EinConfigurationScreenComponent implements OnInit, OnDestroy {
  @Input() pending$: Observable<boolean>;
  pendingSubscription: Subscription;
  pending: boolean;
  @Input() error$: Observable<string | null>;
  errorSubscription: Subscription;
  error: string | null;
  @Input() einConfigData$: Observable<IEinConfiguration[] | null>;
  einConfigDataSubscription: Subscription;
  @Input() formConfigs$: Observable<IFormConfiguration[] | null>;
  formConfigsSubscription: Subscription;

  einGuideStarOrgDetail = [];
  einExceptionOrgDetail = [];

  exCnt: Number = 0;
  einCheckErr = false;

  isLoadingResults = false;
  einExpDate: FormGroup;
  maxDate = moment(new Date()).add(1, 'days').format('YYYY-MM-DD');

  constructor(
    private store: Store<fromEinConfiguration.State>,
    private ref: ChangeDetectorRef,
    private fb: FormBuilder,
    private ams: AngularMaterialService,
    private _dataService: LGEDataService,
    private errorParse: ErrorParseService
  ) {
    this.selectState();
    this.createForms();
  }

  ngOnInit() {
    this.store.dispatch(new EinConfigurationAction.LoadEinConfigurationState());
    this.startSubscriptions();
    // this.GetOrgEINDetails();
  }

  saveExpDate() {
    const date = moment(this.einExpDate.value.startDate.toString()).format(
      'MM/DD/YYYY'
    );
    this.einExpDate.get('startDate').setValue(date);
    this.einExpDate.get('endDate').setValue(date);
    this.ams.showLoader('Saving...');
    this._dataService.FormConfigsIUD(this.einExpDate.value).subscribe({
      next: (response) => {
        this.ams.closeDialog();
        this.ams.showAlert('', 'Successfully Saved');
        this.ams.alert.afterClosed().subscribe((result) => {
          this.ams.showLoader('Verifying exception EINs');
          this.VerifyExceptionEIN(0).finally(() => {
            this.ams.closeDialog();
            this.store.dispatch(new LoadFormConfigState());
            this.store.dispatch(
              new EinConfigurationAction.LoadEinConfigurationState()
            );
          });
        });
      },
      error: (error) => {
        this.ams.closeDialog();
        this.displayError(error);
        // console.log(error);
      },
    });
  }

  VerifyExceptionEIN(cnt): Promise<any> {
    return new Promise((resolve, reject) => {
      if (cnt < this.exCnt) {
        firstValueFrom(this._dataService.VerifyExceptionEIN()).then(
          (result) => {
            this.VerifyExceptionEIN(cnt + 20).finally(() => console.log(cnt));
            resolve(result);
          },
          (err) => {
            this.displayError(err);
            reject(err);
          }
        );
      }
    });
  }

  getTaxExemptStatus(value) {
    if (value === '') return 'Other';
    else if (isNaN(value) || +value === 0) {
      return value;
    } else {
      return `501(c)(${+value})`;
    }
  }
  // Select state
  selectState(): void {
    this.pending$ = this.store.select(
      fromEinConfiguration.getEinConfigurationScreenPending
    );
    this.einConfigData$ = this.store
      .select(fromEinConfiguration.getEinConfigurationState)
      .pipe(
        skipWhile(
          (orgDetails) => orgDetails === null || orgDetails === undefined
        )
      );
    this.formConfigs$ = this.store
      .select(fromAuth.getFormConfigs)
      .pipe(
        skipWhile(
          (orgDetails) => orgDetails === null || orgDetails === undefined
        )
      );
    this.error$ = this.store
      .select(fromEinConfiguration.getEinConfigurationScreenError)
      .pipe(
        skipWhile(
          (error) => error === undefined || error === null || error === ''
        )
      );
  }

  displayLoader(pending: boolean): void {
    if (pending) {
      setTimeout(() => {
        this.ams.showLoader('Loading...');
        this.ref.markForCheck();
      });
    } else {
      this.ams.closeDialog().then(() => {
        if (
          this.error !== null &&
          this.error !== undefined &&
          this.error !== ''
        ) {
          this.displayError(this.error);
        }
      });
    }
  }

  displayError(error: any): void {
    let errorMessage;
    if (this.errorParse.foundKeyWord(error, 'CORPCOMMONWS')) {
      errorMessage = this.errorParse.translateCCError(error);
    } else {
      errorMessage = error instanceof Object ? error.message : error;
    }
    this.ams.showAlert('Loading Error', errorMessage);
    this.ams.alert.afterClosed().subscribe((result) => {
      this.store.dispatch(
        new EinConfigurationAction.EinConfigurationScreenErrorReset()
      );
    });
  }

  // Create reactive forms
  createForms(): void {
    this.einExpDate = this.fb.group({
      name: ['EIN', [Validators.required]],
      startDate: [moment(), [Validators.required]],
      endDate: [moment(), [Validators.required]],
    });
  }
  // Opens sidenav if on mobile
  public openSidenav(): void {
    this.store.dispatch(new lgeApp.OpenSidenav());
  }
  // Subscribe to state data and mark it for changes
  startSubscriptions(): void {
    this.pendingSubscription = this.pending$.subscribe((data) => {
      this.pending = data;
      this.displayLoader(this.pending);
      this.ref.markForCheck();
    });
    this.errorSubscription = this.error$.subscribe((data) => {
      this.error = data;
      this.ref.markForCheck();
    });
    this.einConfigDataSubscription = this.einConfigData$.subscribe((data) => {
      let rawData = JSON.parse(JSON.stringify(data));
      this.einExceptionOrgDetail = rawData.filter(
        (orgInfo) => orgInfo.isException === 1
      );
      this.exCnt = this.einExceptionOrgDetail.filter((r) => !r.isValid).length;
      this.einGuideStarOrgDetail = rawData.filter(
        (orgInfo) => orgInfo.isException === 0
      );
      this.ref.markForCheck();
    });
    this.formConfigsSubscription = this.formConfigs$.subscribe((data) => {
      let expDateVal = JSON.parse(JSON.stringify(data)).find(
        (config) => config.name === 'EIN'
      );
      this.einExpDate.get('startDate').setValue(new Date(expDateVal.startDate));
      this.einExpDate.get('endDate').setValue(new Date(expDateVal.endDate));
      this.ref.markForCheck();
    });
  }
  // Unsubscribe from state data changes
  endSubscriptions(): void {
    this.pendingSubscription.unsubscribe();
    this.errorSubscription.unsubscribe();
    this.einConfigDataSubscription.unsubscribe();
    this.store.dispatch(
      new EinConfigurationAction.EinConfigurationInitialState()
    );
    this._dataService.setEINExceptionFilter('');
  }

  ngOnDestroy() {
    this.endSubscriptions();
  }
}
